/**
 * Order Module Documentation
 */

export const orderDoc = {
  title: 'Order Module',
  content: `# Order Module Training Guide

## 1. Overview

The **Order Module** is the heart of Engraveca's operations. It manages the complete lifecycle of customer orders from creation through fulfillment and payment.

**Key Concept:** An Order represents a customer's purchase. Each order can have line items, a work order (for production), an invoice (for billing), and fulfillment tracking.

---

## 2. Order Lifecycle Overview

\`\`\`mermaid
flowchart TB
    subgraph Phase1["📝 Phase 1: Create"]
        CREATE[Create Order]
        CUST[Select Customer]
        ITEMS[Add Line Items]
        ADDR[Set Ship-To]
    end
    
    subgraph Phase2["⚙️ Phase 2: Process"]
        WO[Generate Work Order]
        PROD[Production]
        INV[Issue Invoice]
    end
    
    subgraph Phase3["📦 Phase 3: Fulfill"]
        SHIP[Ship Order]
        TRACK[Add Tracking]
        NOTIFY[Notify Customer]
    end
    
    subgraph Phase4["💰 Phase 4: Complete"]
        PAY[Record Payment]
        CLOSE[Close Order]
    end
    
    CREATE --> CUST --> ITEMS --> ADDR
    ADDR --> WO --> PROD --> INV
    INV --> SHIP --> TRACK --> NOTIFY
    NOTIFY --> PAY --> CLOSE
    
    style CREATE fill:#fef08a
    style WO fill:#93c5fd
    style INV fill:#c4b5fd
    style SHIP fill:#fde047
    style CLOSE fill:#86efac
\`\`\`

---

## 3. Accessing the Order Module

**Navigation:** Admin Panel → Order Management → **Orders**

---

## 4. Order List View

### 4.1 Dashboard Metrics

| Metric | Description |
|--------|-------------|
| **Open Orders** | Orders not yet completed |
| **In Progress** | Orders currently being worked on |
| **Overdue Invoices** | Invoices past due date |

### 4.2 Order Table Columns

| Column | Description |
|--------|-------------|
| **Order #** | Unique order identifier (e.g., 026-100031) |
| **Customer** | Customer name and email |
| **OPS** | Operations status (Open, In Progress, Completed) |
| **Invoice** | Invoice status (Not Issued, Issued, Paid) |
| **Fulfillment** | Shipping status (None, Shipped) |
| **Due** | Order due date |
| **Total** | Order total amount |

---

## 5. Order Detail View

### 5.1 Order Status Tracks

\`\`\`mermaid
flowchart LR
    subgraph OPS["🔧 Operations Track"]
        O1[Open] --> O2[In Progress] --> O3[Completed]
    end
    
    subgraph INV["💵 Invoice Track"]
        I1[Not Issued] --> I2[Issued] --> I3[Sent] --> I4[Paid]
    end
    
    subgraph FUL["📦 Fulfillment Track"]
        F1[None] --> F2[Ready] --> F3[Shipped]
    end
    
    style O1 fill:#fef08a
    style O3 fill:#86efac
    style I1 fill:#e5e7eb
    style I4 fill:#86efac
    style F1 fill:#e5e7eb
    style F3 fill:#86efac
\`\`\`

### 5.2 Header Section

| Element | Description |
|---------|-------------|
| **Order ID** | Order number with copy button |
| **Edit Order** | Blue button to enter edit mode |
| **Cancel** | Red button to cancel order |
| **Total Card** | Green card showing order total |
| **Due Date Card** | Color-coded due date |
| **Record Payment** | Green button (when invoice issued) |

### 5.3 Action Buttons

| Button | Function |
|--------|----------|
| **Notify Customer** | Send email notifications |
| **Tracking** | Add/edit tracking number |
| **PO#** | Add customer's PO number |
| **Print** | Print documents |

---

## 6. Complete Order Flow

\`\`\`mermaid
flowchart TB
    subgraph Create["1️⃣ CREATE ORDER"]
        A[Select Customer] --> B[Add Items]
        B --> C[Set Ship-To]
        C --> D[Set Due Date]
        D --> E[Save Order]
    end
    
    subgraph Process["2️⃣ PROCESS ORDER"]
        E --> F[Generate Work Order]
        F --> G[Production Works]
        G --> H[Issue Invoice]
        H --> I[Send to Customer]
    end
    
    subgraph Fulfill["3️⃣ FULFILL ORDER"]
        I --> J[Pack Order]
        J --> K[Ship & Add Tracking]
        K --> L[Notify Customer]
    end
    
    subgraph Complete["4️⃣ COMPLETE"]
        L --> M[Customer Pays]
        M --> N[Mark Complete]
    end
    
    style E fill:#fef08a
    style H fill:#c4b5fd
    style K fill:#93c5fd
    style N fill:#86efac
\`\`\`

---

## 7. Creating a New Order

**Steps:**

1. Click **"+ Create New"** → Select **"Order"**
2. Select or create a **Customer**
3. Set **Ship-To address**
4. Add **Line Items**
5. Add **Shipping** charges if applicable
6. Set **Due Date**
7. Click **"Save Order"**

---

## 8. Processing an Order

**Steps:**

1. Open the order detail page
2. Change **OPS Status** to "In Progress"
3. Generate **Work Order** (click "+ Generate")
4. **Issue Invoice** (click "Issue Invoice")
5. **Send Invoice** to customer

---

## 9. Fulfilling an Order

**Steps:**

1. Open the order detail page
2. Change **Fulfillment Status** to "Shipped"
3. Add **Tracking Number**
4. Click **"Notify Customer"**
5. Change **OPS Status** to "Completed"

---

## 10. Recording Payment

\`\`\`mermaid
flowchart LR
    A[Open Order] --> B{Invoice Issued?}
    B -->|No| C[Issue Invoice First]
    C --> B
    B -->|Yes| D[Click Record Payment]
    D --> E[Enter Amount]
    E --> F[Select Method]
    F --> G[Add Reference]
    G --> H[Save]
    H --> I[Status → Paid]
    
    style A fill:#e8d5c4
    style D fill:#86efac
    style I fill:#86efac
\`\`\`

**Steps:**

1. Open order with issued invoice
2. Click **"Record Payment"**
3. Enter payment details
4. Click **"Save"**
5. Invoice status updates to "Paid"

---

## 11. Work Orders

### What is a Work Order?

A Work Order is an internal production document for your team.

\`\`\`mermaid
flowchart LR
    ORDER[📦 Order] --> WO[🔧 Work Order]
    WO --> DEPT[Department]
    WO --> TASKS[Task List]
    WO --> DUE[Due Date]
    WO --> INSTR[Instructions]
    
    style ORDER fill:#93c5fd
    style WO fill:#fef08a
\`\`\`

| Field | Description |
|-------|-------------|
| **WO Number** | Work order ID |
| **Description** | Production instructions |
| **Department** | Assigned department |
| **Due Date** | Production deadline |
| **Tasks** | List of tasks |

---

## 12. Printing Documents

| Document | Description |
|----------|-------------|
| **Order Summary** | Order details (no pricing) |
| **Packing Slip** | Shipping document |
| **Work Order** | Production instructions |
| **Print All** | All documents combined |

---

## 13. Tips & Best Practices

### Do's ✅

- Verify customer and shipping address before processing
- Generate work orders immediately for production
- Add tracking numbers when shipped
- Use notifications to keep customers informed
- Record payments promptly

### Don'ts ❌

- Don't edit orders after invoices are issued
- Don't delete orders - cancel them instead
- Don't skip work order generation
- Don't forget to update status

---

*Last Updated: February 2026*
`
};
